import * as Sentry from '@sentry/react';
import React from 'react';
import { Provider } from 'react-redux';
import ReactDOM from 'react-dom/client';
import { store } from './stores/store';
import { Viewer } from './Viewer/Viewer';
import { ViewerProvider } from './hooks/useViewer/useViewer';
import App from './components/App/App';
import reportWebVitals from './reportWebVitals';
import './index.scss';

// Axe-core dev harness. Logs WCAG violations to the console in development.
// Optional dependency — install with `npm i -D @axe-core/react` to enable.
// Wrapped in a dynamic import so the production bundle never pulls it in.
if (process.env.NODE_ENV === 'development' && !window.Cypress) {
  // The specifier is built at runtime so TypeScript does not resolve the
  // module at compile time when the optional dep is not installed.
  const axePkg = '@axe-core/react';
  import(/* @vite-ignore */ /* webpackChunkName: "axe-a11y" */ axePkg)
    .then((mod: { default: (r: unknown, rd: unknown, n: number) => void }) => {
      mod.default(React, ReactDOM, 1000);
    })
    .catch(() => {
      // Dep not installed; ignore silently so contributors who have not
      // pulled in the optional dev dep are not spammed.
    });
}

declare global {
  interface Window {
    _env: {
      SENTRY_ENV?: string;
      SENTRY_DSN_FRONT_END?: string;
      CLICK_TRACK_SCRIPT_URL?: string;
      GOOGLE_TRACKING_CODE: string;
      MAP_BOX_ACCESS_TOKEN: string;
      FQDN: string;
    };
    Cypress: unknown;
    onCypressInit?: () => void;
    ClickTrack?: {
      init: () => void;
      _trackEvent: (
        category: string,
        action: string,
        data: Record<string, string>
      ) => void;
    };
    APP?: {
      customViewer?: {
        instance?: {
          setPano?: (panoid: string, sceneFade: string) => void;
        };
      };
    };
  }
}

const sentryEnvironment = window._env.SENTRY_ENV || 'development';
const isProdLikeEnvironment = sentryEnvironment !== 'development';

Sentry.init({
  environment: sentryEnvironment,
  dsn: window._env.SENTRY_DSN_FRONT_END,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: isProdLikeEnvironment ? 0.2 : 1.0,
  tracePropagationTargets: [
    'localhost',
    new RegExp(`^https://${window._env.FQDN}/api`),
  ],
  replaysSessionSampleRate: isProdLikeEnvironment ? 0.1 : 1.0,
  replaysOnErrorSampleRate: 1.0,
  denyUrls: [
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
    /^safari-web-extension:\/\//i,
  ],
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    'Non-Error promise rejection captured',
    'top.GLOBALS',
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'atomicFindClose',
    /^Script error\.?$/,
    // Expected user actions Sentry sees as errors
    'The operation was aborted.',
    'Share canceled',
    'Abort due to cancellation of share.',
    'Third-party iframes are not allowed to call share()',
    // Well-known cross-browser quirks
    'NS_ERROR_FAILURE',
    "Can't find variable: EmptyRanges",
    "Right-hand side of 'instanceof' is not an object",
    'The string did not match the expected pattern.',
    'Failed to fetch',
    'Load failed',
    // Browser-extension messaging API
    'runtime.sendMessage',
  ],
  beforeSend(event, hint) {
    const error = hint.originalException;
    const stack = error instanceof Error ? error.stack : undefined;

    if (stack && !stack.includes(window.location.origin)) {
      return null;
    }

    return event;
  },
});

if (window.tour?._id) {
  Sentry.setTag('tourId', window.tour._id);
}

if (window.Cypress) {
  window.onCypressInit?.();
}

Viewer.initViewer();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <Provider store={store}>
    <ViewerProvider>
      <App />
    </ViewerProvider>
  </Provider>
);

reportWebVitals(console.log);
